import { el } from '../../core/dom.js';
import { withProduct } from '../../core/route-map.js';
import { isAdmin, isSuperAdmin } from './util.js';

const COPY = {
  workspace: {
    title: ['Workspace — ', 'how Bonnie talks.'],
    lede: 'Preferred chat language for this organization. Admins can change it anytime.',
    note: 'Org-wide defaults. Team, products, and models stay in the other sections.',
  },
  team: {
    title: ['Team and access — ', 'who sees what.'],
    lede: 'Invite people by email, set their role, and manage who can use each product.',
    note: 'Team, products, models, organizations, billing and support — admin concerns, not daily work.',
  },
  products: {
    title: ['Products — ', 'what you ship.'],
    lede: 'Scan health, open a product to edit it, or archive ones you are not using.',
    note: 'Archive or restore products. Active ones stay in the product switcher.',
  },
  models: {
    title: ['AI models — ', 'what answers.'],
    lede: 'Default catalog, enablement, and whether a provider key is set for this workspace.',
    note: 'Sets what appears in every chat\'s model picker, org-wide.',
  },
  organizations: {
    title: ['Organizations — ', 'every tenant.'],
    lede: 'Browse and open CraftisanLabs tenant workspaces.',
    note: 'Super-admin only. Connectors and knowledge stay in the sidebar Settings dropdown.',
  },
  billing: {
    title: ['Billing — ', 'plan and usage.'],
    lede: 'Plan label, seat count, and where your own API keys billing lives until self-serve invoices ship.',
    note: 'Plan and seats from the workspace — invoices come later.',
  },
  support: {
    title: ['Support — ', 'talk to us.'],
    lede: 'Message CraftisanLabs in-app, email the team, or book a live setup session.',
    note: 'Chat with CraftisanLabs or book a live setup session.',
  },
  notifications: {
    title: ['Notifications — ', 'what reaches you.'],
    lede: 'Email for mentions and finished reports, plus the weekly digest.',
    note: 'Yours to change. Team, products, and models stay admin concerns.',
  },
  audit: {
    title: ['Audit log — ', 'what changed.'],
    lede: 'Actions in this workspace, newest first. Filter and export the current view.',
    note: 'Admins only. Passwords and secrets are left out of the detail column.',
  },
  activity: {
    title: ['User activity — ', 'who showed up.'],
    lede: 'Sign-ups, active days, and the people behind the most actions.',
    note: 'Admins only. 30 or 90 days.',
  },
};

/**
 * @param {string} section
 * @param {{ productId: string, me: object, onInvite?: () => void, onAddProduct?: () => void, isAdmin: boolean }} opts
 */
export function renderHeader(section, opts) {
  const copy = COPY[section] || COPY.team;
  let action = null;
  if (section === 'team' && opts.isAdmin && opts.onInvite) {
    action = el('button', { type: 'button', class: 'set-cta', onClick: opts.onInvite },
      'Invite Someone',
      el('span', { class: 'set-arw', 'aria-hidden': 'true' }, '→'));
  }
  if (section === 'products' && opts.isAdmin && opts.onAddProduct) {
    action = el('a', {
      class: 'set-cta',
      href: withProduct('/app/products/new', opts.productId),
    }, 'Add Product', el('span', { class: 'set-arw', 'aria-hidden': 'true' }, '→'));
  }
  return el('header', { class: 'set-head' },
    el('div', { class: 'set-head-copy' },
      el('h1', { class: 'set-title' },
        copy.title[0],
        el('em', {}, copy.title[1])),
      el('p', {}, copy.lede)),
    action);
}

/**
 * Inner Settings menu. Order is owner-set; Organizations is super-admin only.
 * @param {string} section
 * @param {{ productId: string, me: object }} opts
 */
export function renderSubnav(section, opts) {
  const items = settingsNavItems(opts.me, opts.productId);
  const note = (COPY[section] || COPY.workspace).note;
  return el('nav', { class: 'set-subnav', 'aria-label': 'Settings sections' },
    items.map((item) => {
      const on = item.id === section;
      return el('a', {
        class: on ? 'set-sub is-on' : 'set-sub',
        href: item.href,
        'aria-current': on ? 'page' : null,
      },
      item.label,
      on ? el('span', { class: 'set-dot', 'aria-hidden': 'true' }) : null);
    }),
    el('p', { class: 'set-subnav-note' }, note));
}

const BASE_NAV = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'team', label: 'Team and access' },
  { id: 'products', label: 'Products' },
  { id: 'models', label: 'AI models' },
];

const TAIL_NAV = [
  { id: 'billing', label: 'Billing' },
  { id: 'support', label: 'Support' },
];

const ADMIN_NAV = [
  { id: 'audit', label: 'Audit log' },
  { id: 'activity', label: 'User activity' },
];

/** @param {object|null|undefined} me @param {string} productId */
export function settingsNavItems(me, productId) {
  const items = [
    ...BASE_NAV,
    ...(isSuperAdmin(me) ? [{ id: 'organizations', label: 'Organizations' }] : []),
    ...TAIL_NAV,
    ...(isAdmin(me) ? ADMIN_NAV : []),
  ];
  return items.map((item) => ({
    ...item,
    href: withProduct(`/app/settings/${item.id}`, productId),
  }));
}

/** Exported for tests. */
export function settingsSubnavIds(me) {
  return settingsNavItems(me, '').map((item) => item.id);
}
